import type {Offer} from '@yandex-delivery/api/model/offer'
import {Translator} from '@yandex-delivery/core/i18n'
import {formatDate} from '@yandex-delivery/utils'
import {dateWithTimeZone} from '@yandex-delivery/utils/timezone'

type FIXME_Dayjs = {
  format(f: string): string
}
type OfferTimeData = {
  full: string
  short: string
}
type Time = {
  hours?: number
  minutes: number
}

// не много синглтона не помешает
let i18n: Translator = function i18n() {
  throw new Error('Function "i18n" not implemented.')
}

// fixme это должен быть метод модели, ни как не связанный с вьюхой
//       но пока нет нормальной модели офферов, терпим такой подход
//       используется в ЛК и ордере
export function formatOfferInterval(t: Translator) {
  i18n = t

  return function _formatOfferInterval(
    tariff: string,
    intervals: Offer['visitingIntervals'] = [],
    isDue?: boolean,
    hours?: number,
  ): OfferTimeData {
    if (isDue) {
      return toDueFormat(intervals, tariff.includes('cargo_hour') ? 'cargo_hour' : undefined, hours)
    }

    if (tariff.includes('express_d2d')) {
      return toExpressFormat(intervals)
    }

    if (tariff.includes('sdd')) {
      return toSddFormat(intervals)
    }

    if (tariff.includes('cargo_hour')) {
      return toCargoFormat(hours)
    }

    return toDefaultFormat(intervals)
  }
}

function getRelativeTimeString(minutes: string, hours?: string) {
  const result: string[] = []

  if (hours) {
    result.push(
      `${hours} ${i18n('create.offers.relative-time.hours', {
        count: Number(hours),
      })}`,
    )
  }

  result.push(
    i18n('create.offers.relative-time.minutes', {
      placeholder: {
        minutes,
      },
    }),
  )

  return result.join(' ')
}

const ABSOLUTE_ROUND_BY = 15
const RELATIVE_ROUND_BY = 5
const RELATIVE_INTERVAL_STEP = 10

function formatTime(date: FIXME_Dayjs): string {
  return date.format('HH:mm')
}

function getRoundedDate(datetime: string): FIXME_Dayjs {
  const date = dateWithTimeZone(datetime).startOf('minutes')
  const minutes = date.minute()
  const remainder = minutes + ABSOLUTE_ROUND_BY
  const divideRemainderWithRoundResult = minutes % ABSOLUTE_ROUND_BY

  if (divideRemainderWithRoundResult === 0) {
    return date
  }

  const roundedMinutes = Math.floor(remainder / ABSOLUTE_ROUND_BY) * ABSOLUTE_ROUND_BY
  const dateWithoutMinutes = date.startOf('hour')

  return dateWithoutMinutes.add(roundedMinutes, 'minutes')
}

function getTime(minutes: number): Time {
  const hours = Math.floor(minutes / 60)

  if (hours === 0 || minutes === 60) {
    return {minutes}
  }

  return {
    hours,
    minutes: minutes % 60,
  }
}

function toRelativeTime(fromDate: string, toDate: string): [string | undefined, string] {
  const dateFrom = dateWithTimeZone(fromDate)
  const dateTo = dateWithTimeZone(toDate)
  const minutesDiff = dateTo.diff(dateFrom, 'minute')
  const remainder = minutesDiff + RELATIVE_ROUND_BY
  const roundedMinutes = Math.floor(remainder / RELATIVE_ROUND_BY) * RELATIVE_ROUND_BY
  const highest = Math.ceil(roundedMinutes / RELATIVE_INTERVAL_STEP) * RELATIVE_INTERVAL_STEP
  const lowest = highest - RELATIVE_INTERVAL_STEP
  const highestTime = getTime(highest)
  const lowestTime = getTime(lowest)

  if (lowestTime.minutes === 60 && lowestTime.hours === undefined) {
    return [highestTime.hours ? String(highestTime.hours) : undefined, String(highestTime.minutes)]
  }

  return [
    lowestTime.hours ? String(lowestTime.hours) : undefined,
    [lowestTime.minutes, lowestTime.minutes + RELATIVE_INTERVAL_STEP === 60 ? 60 : highestTime.minutes]
      .filter(Boolean)
      .join('-'),
  ]
}

function getAbsoluteTimeString(fromDate: string, toDate?: string): string {
  return toDate
    ? `${formatTime(getRoundedDate(fromDate))}-${formatTime(getRoundedDate(toDate))}`
    : formatTime(getRoundedDate(fromDate))
}

function toDueFormat(
  [pickupInterval, deliveryInterval]: TimeInterval[],
  tariff?: string,
  hours?: number,
): OfferTimeData {
  const timePickup = getAbsoluteTimeString(pickupInterval.from, pickupInterval.to)
  const timePickupCargo = getAbsoluteTimeString(pickupInterval.from)
  const datePickupCargo = formatDate('D MMMM')(pickupInterval.from)
  const [deliveryTimeHours, deliveryTimeMinutes] = toRelativeTime(deliveryInterval.from, deliveryInterval.to)
  const timeDelivery = getRelativeTimeString(deliveryTimeMinutes, deliveryTimeHours)

  if (tariff === 'cargo_hour') {
    return {
      full: `${datePickupCargo}, ${timePickupCargo}`,
      short: i18n('create.offers.offer.due.cargo-hour.short', {
        placeholder: {
          hours,
        },
        count: hours,
      }),
    }
  }

  return {
    full: i18n('create.offers.offer.due.full', {
      placeholder: {
        timePickup,
        timeDelivery,
      },
    }),
    short: i18n('create.offers.offer.due.short', {
      placeholder: {
        timeDelivery,
      },
    }),
  }
}

function toCargoFormat(hours?: number): OfferTimeData {
  return {
    full: i18n('create.offers.offer.cargo-hour.full', 'Приедет через 10–30 мин'),
    short: i18n('create.offers.offer.due.cargo-hour.short', {
      placeholder: {
        hours,
      },
      count: hours,
    }),
  }
}

function toSddFormat([pickupInterval, deliveryInterval]: Offer['visitingIntervals']): OfferTimeData {
  const timePickup = getAbsoluteTimeString(pickupInterval.to)
  const timeDelivery = getAbsoluteTimeString(deliveryInterval.to)

  return {
    full: i18n('create.offers.offer.sdd.full', {
      placeholder: {
        timePickup,
        timeDelivery,
      },
    }),
    short: i18n('create.offers.offer.sdd.short', {
      placeholder: {
        timeDelivery,
      },
    }),
  }
}

function toExpressFormat([pickupInterval, deliveryInterval]: Offer['visitingIntervals']): OfferTimeData {
  const [pickupHours, pickupMinutes] = toRelativeTime(pickupInterval.from, pickupInterval.to)
  const [deliveryHours, deliveryMinutes] = toRelativeTime(deliveryInterval.from, deliveryInterval.to)
  const timePickup = getRelativeTimeString(pickupMinutes, pickupHours)
  const timeDelivery = getRelativeTimeString(deliveryMinutes, deliveryHours)

  return {
    full: i18n('create.offers.offer.express.full', {
      placeholder: {
        timePickup,
        timeDelivery,
      },
    }),
    short: i18n('create.offers.offer.express.short', {
      placeholder: {
        timeDelivery,
      },
    }),
  }
}

function toDefaultFormat([, deliveryInterval]: Offer['visitingIntervals']): OfferTimeData {
  const [hours, minutes] = toRelativeTime(deliveryInterval.from, deliveryInterval.to)
  const time = getRelativeTimeString(minutes, hours)

  return {
    full: i18n('create.offers.offer.default.full', {
      placeholder: {time},
    }),
    short: i18n('create.offers.offer.default.short', {
      placeholder: {time},
    }),
  }
}
